import { Box, Button, Chip, Dialog, DialogContent, Divider, Grid, IconButton, Paper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close, DeleteOutline, Inventory2Outlined, LanguageRounded, MoreVert, ReceiptLong } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { STATUS_SALE } from '../../../../helpers/enums';
import { ccyFormat } from '../../../../helpers/helper';
import { colorDiego, colorJazmin, colorMary, colorSusy } from '../../../../styles';
import TicketPreviewModal from '../../../tickets/TicketPreviewModal';

// ── vendor map (color + name, same IDs as SaleData.getColorVendor) ───────────
const VENDOR_MAP = {
  '6653654e72dfadfe67d93069': { color: colorJazmin, name: 'Jazmin' },
  '6657ff19ac884bfd7574f4c1': { color: colorDiego,  name: 'Diego'  },
  '6657ff27ac884bfd7574f4c2': { color: colorMary,   name: 'Mary'   },
  '665d3374fe889a105f3c7587': { color: colorSusy,   name: 'Susy'   },
};
const getVendor = (userId) => VENDOR_MAP[userId] || null;

// ── time of day (same logic as SaleData.past16Hs) ────────────────────────────
// ── time of day (mirrors SaleData.past16Hs / SaleRow which uses updated_at for FINISHED) ─
// For delivered/canceled sales the relevant time is updated_at (when status changed).
// For pending/reserved sales it is the creation time (created_sale || created_at).
const getSaleHour = (sale) => {
  const isFinishedOrCanceled =
    sale.status === STATUS_SALE.FINISHED || sale.status === STATUS_SALE.CANCELED;
  const raw = isFinishedOrCanceled
    ? (sale.updated_at || sale.created_sale || sale.created_at)
    : (sale.created_sale || sale.created_at);
  if (!raw) return null;
  // date-only strings (YYYY-MM-DD) carry no hour info → skip
  if (typeof raw === 'string' && raw.length <= 10) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getHours();
};
// Mañana < 16h  →  skyblue  |  Tarde ≥ 16h  →  gray (matching SaleData.stylePast16)

// ── same color logic as SaleData styleGroupByDate / styleRead ─────────────────
const GREEN        = { accent: '#2F7D4E', light: '#C6EFCE', text: '' };
const RED          = { accent: '#B44C58', light: '#FDECEF', text: '' };
const RED_ALERT    = { accent: '#9C0006', light: '#FFC7CE', text: '' }; // expired reserved — matches SaleData #FFC7CE / #9C0006
const RED_RETURNED = { accent: '#B44C58', light: '#FCE3E8', text: '' };
const DEPOSIT_CHIP_COLOR = '#FFF4CC';
const DEPOSIT_CHIP_BORDER = '#F1D37A';
const PENDING_CHIP_COLOR = '#FFC7CE';
const PROGRESS_PAID_COLOR = '#FFB81C';
const PROGRESS_PENDING_COLOR = '#FF5252';

const parseYmd = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const ymdToLocalDate = (value) => {
  const parsed = parseYmd(value);
  if (!parsed) return null;
  const d = new Date(parsed.year, parsed.month - 1, parsed.day);
  return Number.isNaN(d.getTime()) ? null : d;
};

const getCardCfg = (saleList, dateGroupKeys) => {
  const status = saleList[0]?.status;

  if (status === STATUS_SALE.FINISHED) return { ...GREEN, text: 'Finalizada' };
  if (status === STATUS_SALE.CANCELED) return { ...RED,   text: 'Cancelada' };

  // RESERVED — replicate SaleData logic exactly
  const hasReturnedSales = saleList.some((s) => s.status === STATUS_SALE.RETURNED);
  if (hasReturnedSales) return { ...RED_RETURNED, text: 'Con devolución' };

  const today = new Date();
  const anyExpired = dateGroupKeys.some((dateString) => {
    if (!dateString || dateString === 'Sin fecha') return false;
    const d = new Date(dateString); // mirrors SaleData.checkDatesFromSales exactly
    if (isNaN(d.getTime())) return false;
    return (today - d) / (1000 * 60 * 60 * 24) > 7;
  });
  const hasDateAlert = dateGroupKeys.length > 2 || anyExpired;

  if (hasDateAlert) return { ...RED_ALERT, text: 'Reservada' };
  return              { ...GREEN,     text: 'Reservada' };
};

// ── helpers ───────────────────────────────────────────────────────────────────
const getDateKey = (rawDate) => {
  if (!rawDate) return '';
  if (typeof rawDate === 'string') {
    const trimmed = rawDate.trim();
    if (!trimmed) return '';
    // Keep raw day part exactly as provided by backend (same as SaleData mapDate).
    return trimmed.substring(0, 10);
  }

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return '';

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fmtShortDateKey = (dateKey) => {
  if (!dateKey || dateKey.length < 10 || dateKey === 'Sin fecha') return '';
  const parsed = ymdToLocalDate(dateKey);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('es-AR', { day: 'numeric', month: 'numeric' });
};

const groupByDate = (sales) => {
  const result = {};
  sales.forEach((sale) => {
    // Try all possible date fields in priority order
    const raw = sale.created_at || sale.created_sale || sale.date || sale.createdAt || sale.updatedAt || sale.updated_at;
    const dateKey = getDateKey(raw) || 'Sin fecha';
    if (!result[dateKey]) result[dateKey] = [];
    result[dateKey].push({ ...sale, created_sale: raw });
  });
  return result;
};

const getPendingBySale = (sale) =>
  Math.max((sale.prices_sold?.sale_price || 0) * sale.quantity_sold - (sale.deposit || 0), 0);

const formatDetailDate = (value) => {
  if (!value || value === 'Sin fecha') return 'Sin fecha';
  const parsed = ymdToLocalDate(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: 'short' });
};

// ── single card ───────────────────────────────────────────────────────────────
const SaleCard = ({ groupKey, groupData, onOpenMenu, canMenu, onOpenDetail, onPreviewTicket, showPaidWatermark = false }) => {
  const saleList = groupData.sales || [];
  const nameSale = saleList[0]?.nameSale || groupKey;
  const vendor = getVendor(saleList[0]?.userId || '');
  const vendorColor = vendor?.color || '';

  // time-of-day: mañana (<16h) or tarde (>=16h) — one sale decides the group
  const hours = saleList.map(getSaleHour).filter((h) => h !== null);
  const hasMorning   = hours.some((h) => h < 16);
  const hasAfternoon = hours.some((h) => h >= 16);

  const total = saleList.reduce((acc, s) => acc + (s.prices_sold?.sale_price || 0) * s.quantity_sold, 0);
  const deposit = saleList.reduce((acc, s) => acc + (s.deposit || 0), 0);
  const totalQty = saleList.reduce((acc, s) => acc + s.quantity_sold, 0);
  const status = saleList[0]?.status;
  const hasDeposit = deposit > 0 && status === STATUS_SALE.RESERVED;
  const isFinishedHeld = status === STATUS_SALE.FINISHED && saleList.some((sale) => sale.held);
  const pending = hasDeposit ? Math.max(total - deposit, 0) : 0;
  const paidAmount = status === STATUS_SALE.RESERVED ? deposit : total;
  const paidPercent = total > 0 ? Math.min((paidAmount / total) * 100, 100) : 0;
  const pendingPercent = Math.max(100 - paidPercent, 0);

  const dateGroupsMap = groupByDate(saleList);
  const dateGroupKeys = Object.keys(dateGroupsMap);
  const cfg = getCardCfg(saleList, dateGroupKeys);

  const webOrderCount = saleList.filter((s) => s?.origin === 'transfer_order').length;
  const hasWebOrders = webOrderCount > 0;
  const allWeb = webOrderCount === saleList.length;

  const dateKeys = dateGroupKeys
    .filter((k) => k && k !== 'Sin fecha')
    .sort();

  const formattedDates = dateKeys.map(fmtShortDateKey).filter(Boolean);

  const dateLabel =
    formattedDates.length === 0
      ? ''
      : formattedDates.length > 2
      ? formattedDates.join(' · ')
      : formattedDates.length === 1
      ? formattedDates[0]
      : `${formattedDates[0]} – ${formattedDates[formattedDates.length - 1]}`;

  return (
    <Paper
      elevation={0}
      onClick={() => onOpenDetail(groupKey)}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        border: '1.5px solid',
        borderColor: cfg.accent + '44',
        borderRadius: 2.5,
        overflow: 'hidden',
        bgcolor: cfg.light,
        backgroundImage: `linear-gradient(180deg, ${cfg.light} 0%, ${cfg.light} 100%)`,
        boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        display: 'flex',
        flexDirection: 'row',
        '&:hover': {
          borderColor: cfg.accent + 'bb',
          boxShadow: `0 4px 10px ${cfg.accent}1f`,
        },
      }}
    >
      {/* vendor color strip */}
      {vendorColor && (
        <Box sx={{ width: 5, flexShrink: 0, bgcolor: vendorColor, alignSelf: 'stretch' }} />
      )}
      {showPaidWatermark && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', zIndex: 10,
        }}>
          <Typography sx={{
            fontWeight: 900, fontSize: '1.4rem', letterSpacing: '4px',
            color: 'rgba(46,125,50,0.22)', border: '3px solid rgba(46,125,50,0.22)',
            px: 1.5, py: 0.2, borderRadius: 1.5,
            transform: 'rotate(-25deg)', userSelect: 'none', whiteSpace: 'nowrap',
          }}>PAGADO</Typography>
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* top progress bar indicator (seña vs debe) */}
      {hasDeposit && (
        <Box sx={{ height: 4, display: 'flex', width: '100%' }}>
          <Box
            sx={{
              width: `${paidPercent}%`,
              bgcolor: PROGRESS_PAID_COLOR,
              transition: 'width 0.2s ease',
            }}
          />
          <Box
            sx={{
              width: `${pendingPercent}%`,
              bgcolor: PROGRESS_PENDING_COLOR,
              transition: 'width 0.2s ease',
            }}
          />
        </Box>
      )}


      <Box sx={{ px: { xs: 1, sm: 1.2 }, pt: { xs: 0.8, sm: 0.95 }, pb: { xs: 0.55, sm: 0.7 } }}>
        {/* name + chips + menu button */}
        <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={0.4}>
          <Box display='flex' alignItems='center' gap={0.45} flexWrap='wrap' flex={1} minWidth={0}>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: '0.84rem', sm: '0.92rem' },
                lineHeight: 1.2,
                color: '#111',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                display: '-webkit-box',
                WebkitLineClamp: { xs: 2, sm: 1 },
                WebkitBoxOrient: 'vertical',
              }}
            >
              {nameSale}
            </Typography>
            {hasWebOrders && (
              <Tooltip title={allWeb ? 'Orden web' : `${webOrderCount} ítem(s) desde web`} placement='top'>
                <Chip
                  size='small'
                  icon={<LanguageRounded sx={{ fontSize: '0.82rem !important', color: '#fff !important' }} />}
                  label={allWeb ? '' : String(webOrderCount)}
                  sx={{
                    height: { xs: 19, sm: 21 },
                    bgcolor: 'info.dark',
                    color: '#fff',
                    border: '1px solid',
                    borderColor: 'info.dark',
                    borderRadius: 1.5,
                    '& .MuiChip-label': {
                      px: allWeb ? 0.3 : 0.8,
                      fontSize: '0.67rem',
                      fontWeight: 700,
                    },
                    '& .MuiChip-icon': { ml: 0.5 },
                  }}
                />
              </Tooltip>
            )}
            {hasDeposit && (
              <>
                <Chip
                  size='small'
                  label={`Seña $${ccyFormat(deposit)}`}
                  sx={{
                    height: { xs: 19, sm: 21 },
                    bgcolor: DEPOSIT_CHIP_COLOR,
                    color: '#9A6700',
                    border: `1px solid ${DEPOSIT_CHIP_BORDER}`,
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      px: { xs: 0.65, sm: 0.82 },
                      fontSize: { xs: '0.62rem', sm: '0.68rem' },
                      fontWeight: 800,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
                {pending > 0 && (
                  <Chip
                    size='small'
                    label={`Debe $${ccyFormat(pending)}`}
                    sx={{
                      height: { xs: 19, sm: 21 },
                      bgcolor: PENDING_CHIP_COLOR,
                      color: '#9C0006',
                      border: '1px solid #E5A5AE',
                      maxWidth: '100%',
                      '& .MuiChip-label': {
                        px: { xs: 0.65, sm: 0.82 },
                        fontSize: { xs: '0.62rem', sm: '0.68rem' },
                        fontWeight: 800,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                )}
              </>
            )}
          </Box>
          {canMenu && (
            <IconButton
              size='small'
              sx={{ p: { xs: 0.05, sm: 0.15 }, mt: -0.25, color: cfg.accent + 'cc', flexShrink: 0 }}
              onClick={(e) => { e.stopPropagation(); onOpenMenu(e, groupKey); }}
            >
              <MoreVert sx={{ fontSize: { xs: 21, sm: 24 } }} />
            </IconButton>
          )}
          {isFinishedHeld && onPreviewTicket && (
            <Tooltip title='Imprimir ticket' placement='top'>
              <IconButton
                size='small'
                sx={{ p: { xs: 0.05, sm: 0.15 }, mt: -0.25, color: cfg.accent + 'cc', flexShrink: 0 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onPreviewTicket(saleList[0]?.store_id, saleList.map((sale) => sale.idSale).filter(Boolean));
                }}
              >
                <ReceiptLong sx={{ fontSize: { xs: 20, sm: 22 } }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* total */}
        <Typography
          sx={{ fontWeight: 900, fontSize: { xs: '1.05rem', sm: '1.22rem' }, color: cfg.accent, lineHeight: 1.1, mt: 0.35 }}
        >
          ${ccyFormat(total)}
        </Typography>

        {/* footer: counts + badges */}
        <Box display='flex' alignItems='center' flexWrap='wrap' gap={0.5} mt={0.3}>
          <Typography sx={{ fontSize: { xs: '0.71rem', sm: '0.78rem' }, color: '#555', fontWeight: 600 }}>
            {totalQty} uds · {saleList.length} {saleList.length === 1 ? 'art.' : 'arts.'}
          </Typography>
          {dateLabel && (
            <Typography sx={{ fontSize: { xs: '0.71rem', sm: '0.78rem' }, color: '#888', fontWeight: 500 }}>
              · {dateLabel}
            </Typography>
          )}
          {/* vendor chip */}
          {vendor && (
            <Chip
              size='small'
              label={vendor.name}
              sx={{
                height: 17,
                bgcolor: vendor.color,
                color: '#222',
                fontWeight: 800,
                border: '1px solid rgba(0,0,0,0.12)',
                '& .MuiChip-label': { px: 0.7, fontSize: '0.6rem' },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
    {/* time-of-day strip — right side, sibling to content box, mirrors SaleRow */}
    {(hasMorning || hasAfternoon) && (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: 3, flexShrink: 0, alignSelf: 'stretch' }}>
        {hasMorning   && <Box sx={{ flex: 1, bgcolor: 'skyblue' }} />}
        {hasAfternoon && <Box sx={{ flex: 1, bgcolor: '#9CA3AF' }} />}
      </Box>
    )}
    </Paper>
  );
};

const DetailTable = ({ localSales, groupData, onDeleteItem, canDelete }) => {
  const dateEntries = Object.entries(localSales || {});

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr) 42px 76px 96px 32px 3px', sm: 'minmax(0, 1fr) 48px 92px 124px 36px 3px' },
          gap: 0.75,
          px: { xs: 1.45, sm: 1.9 },
          py: 0.85,
          borderBottom: '1px solid',
          borderColor: 'rgba(0,0,0,0.12)',
          bgcolor: '#EEF2F7',
        }}
      >
        <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.8rem' }, fontWeight: 900, color: '#4B5563' }}>Articulo</Typography>
        <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.8rem' }, fontWeight: 900, color: '#4B5563', textAlign: 'center' }}>Cant</Typography>
        <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.8rem' }, fontWeight: 900, color: '#4B5563', textAlign: 'right' }}>Total</Typography>
        <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.8rem' }, fontWeight: 900, color: '#4B5563', textAlign: 'right' }}>Pago</Typography>
        <Typography sx={{ fontSize: { xs: '0.74rem', sm: '0.8rem' }, fontWeight: 900, color: '#4B5563', textAlign: 'center' }}>Acc.</Typography>
      </Box>

      {dateEntries.map(([date, saleGroup]) => {
        const groupTotal = saleGroup.reduce(
          (acc, s) => acc + (s.prices_sold?.sale_price || 0) * s.quantity_sold,
          0
        );

        return (
          <Box key={date}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: { xs: 1.45, sm: 1.9 },
                py: 0.7,
                bgcolor: 'rgba(0,0,0,0.055)',
                borderBottom: '1px solid',
                borderColor: 'rgba(0,0,0,0.1)',
              }}
            >
              <Typography sx={{ fontSize: { xs: '0.76rem', sm: '0.82rem' }, fontWeight: 800, color: '#4B5563', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                {formatDetailDate(date)}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.78rem', sm: '0.84rem' }, fontWeight: 900, color: '#374151' }}>
                ${ccyFormat(groupTotal)}
              </Typography>
            </Box>

            {saleGroup.map((sale, idx) => {
              const total = (sale.prices_sold?.sale_price || 0) * sale.quantity_sold;
              const hasDeposit = (sale.deposit || 0) > 0 && sale.status === STATUS_SALE.RESERVED;
              const pending = hasDeposit ? getPendingBySale(sale) : 0;
              const imageUrl = sale?.article?.images?.[0] || '';

              return (
                <Box
                  key={sale._id || `${sale.article?.id || sale.article?.name}-${sale.created_at}-${idx}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr) 42px 76px 96px 32px 3px', sm: 'minmax(0, 1fr) 48px 92px 124px 36px 3px' },
                    gap: 0.75,
                    alignItems: 'center',
                    px: { xs: 1.45, sm: 1.9 },
                    py: 0.95,
                    borderBottom: '1px dashed',
                    borderColor: 'rgba(0,0,0,0.11)',
                    bgcolor: idx % 2 ? 'rgba(17,24,39,0.03)' : 'transparent',
                  }}
                >
                  <Typography
                    onClick={() => {
                      if (imageUrl) window.open(imageUrl, '_blank');
                    }}
                    sx={{
                      fontSize: { xs: '0.9rem', sm: '0.98rem' },
                      fontWeight: 700,
                      color: '#111827',
                      overflow: 'visible',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      lineHeight: 1.25,
                      cursor: imageUrl ? 'pointer' : 'default',
                      textDecoration: imageUrl ? 'underline dotted' : 'none',
                      textUnderlineOffset: imageUrl ? '2px' : '0',
                    }}
                  >
                    {sale.article?.name || 'Sin nombre'}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.86rem', sm: '0.94rem' }, fontWeight: 800, color: '#4B5563', textAlign: 'center' }}>
                    {sale.quantity_sold}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: '0.92rem', sm: '1.02rem' }, fontWeight: 900, color: '#111827', textAlign: 'right' }}>
                    ${ccyFormat(total)}
                  </Typography>
                  <Box sx={{ textAlign: 'right', minHeight: 24, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.35 }}>
                    {hasDeposit ? (
                      <>
                        <Chip
                          size='small'
                          label={`Seña $${ccyFormat(sale.deposit || 0)}`}
                          sx={{
                            height: { xs: 20, sm: 22 },
                            bgcolor: '#FFF4CC',
                            color: '#9A6700',
                            border: '1px solid #F1D37A',
                            '& .MuiChip-label': {
                              px: { xs: 0.75, sm: 0.9 },
                              fontSize: { xs: '0.66rem', sm: '0.72rem' },
                              fontWeight: 900,
                            },
                          }}
                        />
                        {pending > 0 && (
                          <Chip
                            size='small'
                            label={`Debe $${ccyFormat(pending)}`}
                            sx={{
                              height: { xs: 20, sm: 22 },
                              bgcolor: '#FFC7CE',
                              color: '#9C0006',
                              border: '1px solid #E5A5AE',
                              '& .MuiChip-label': {
                                px: { xs: 0.75, sm: 0.9 },
                                fontSize: { xs: '0.66rem', sm: '0.72rem' },
                                fontWeight: 900,
                              },
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.76rem' }, fontWeight: 700, color: '#9CA3AF' }}>
                        -
                      </Typography>
                    )}
                  </Box>
                  <Box display='flex' justifyContent='center'>
                    {canDelete && (
                      <IconButton
                        size='small'
                        color='error'
                        onClick={() => {
                          const confirmDelete = window.confirm(`Quitar "${sale.article?.name || 'articulo'}" del pedido?`);
                          if (confirmDelete) onDeleteItem && onDeleteItem(sale, groupData);
                        }}
                        sx={{ p: { xs: 0.2, sm: 0.35 } }}
                      >
                        <DeleteOutline sx={{ fontSize: { xs: 16, sm: 18 } }} />
                      </IconButton>
                    )}
                  </Box>
                  {/* time-of-day strip — right side */}
                  {(() => {
                    const h = getSaleHour(sale);
                    const color = h === null ? 'transparent' : h >= 16 ? '#9CA3AF' : 'skyblue';
                    return <Box sx={{ alignSelf: 'stretch', bgcolor: color, borderRadius: 0.5 }} />;
                  })()}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

// ── detail dialog ────────────────────────────────────────────────────────────
const DetailDialog = ({ groupKey, groupData, onClose, onDeleteItem, canDelete, onDeliverHeld }) => {
  const theme = useTheme();
  const isSmallPhone = useMediaQuery(theme.breakpoints.down('sm'));

  if (!groupData) return null;
  const saleList = groupData.sales || [];
  const nameSale = saleList[0]?.nameSale || groupKey;
  const total = saleList.reduce((acc, s) => acc + (s.prices_sold?.sale_price || 0) * s.quantity_sold, 0);
  const localSales = groupByDate(saleList);
  const dateGroupKeys = Object.keys(localSales);
  const cfg = getCardCfg(saleList, dateGroupKeys);
  const isFinishedHeld = saleList.length > 0 && saleList[0]?.status === STATUS_SALE.FINISHED && saleList.some(s => s.held)

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallPhone}
      maxWidth='sm'
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden',
          m: { xs: 0, sm: 1.5 },
        },
      }}
    >
      {/* header */}
      <Box sx={{ bgcolor: cfg.accent, px: { xs: 1.5, sm: 2 }, pt: { xs: 1.4, sm: 1.75 }, pb: { xs: 1.2, sm: 1.5 } }}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Box display='flex' alignItems='center' gap={1}>
            <Box
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.78rem', sm: '0.85rem' }, color: 'white' }}>
                {nameSale[0]?.toUpperCase() ?? '?'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '0.98rem' }, color: 'white', lineHeight: 1.2 }}>
                {nameSale}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.68rem', sm: '0.74rem' }, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                {cfg.text}
              </Typography>
            </Box>
          </Box>
          <Box textAlign='right'>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.26rem' }, color: 'white', lineHeight: 1 }}>
              ${ccyFormat(total)}
            </Typography>
            <IconButton
              size='small'
              onClick={onClose}
              sx={{ color: 'rgba(255,255,255,0.8)', p: 0.25, mt: 0.25 }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* items */}
      <DialogContent sx={{ p: 0 }}>
        <DetailTable
          localSales={localSales}
          groupData={groupData}
          onDeleteItem={onDeleteItem}
          canDelete={canDelete}
        />
      </DialogContent>
      {isFinishedHeld && onDeliverHeld && (
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            size='small'
            startIcon={<Inventory2Outlined />}
            onClick={async () => { await onDeliverHeld(saleList); onClose(); }}
            sx={{ bgcolor: '#3949ab', '&:hover': { bgcolor: '#283593' }, borderRadius: 2, fontWeight: 700, fontSize: '0.78rem' }}
          >
            Entregar del depósito
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

// ── grid root ─────────────────────────────────────────────────────────────────
const CashierSaleGrid = ({ sales, onOpenMenu, canMenu, onDeleteItem, canDeleteItem, showPaidWatermark = false, onDeliverHeld }) => {
  const [detailKey, setDetailKey] = useState(null);
  const [previewSale, setPreviewSale] = useState(null);
  const groupKeys = Object.keys(sales || {});

  const handlePreviewTicket = (storeId, idSales) => {
    if (!storeId || !idSales?.length) return;
    setPreviewSale({ storeId, idSales });
  };

  // Group keys by first letter of nameSale, sorted alphabetically
  const alphabetGroups = useMemo(() => {
    const groups = {};
    groupKeys.forEach((key) => {
      const nameSale = sales[key]?.sales?.[0]?.nameSale || key;
      const letter = nameSale[0]?.toUpperCase() || '#';
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(key);
    });
    return Object.fromEntries(Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, 'es')));
  }, [groupKeys, sales]);

  return (
    <>
      <Box
        sx={{
          px: { xs: 0.6, sm: 1.1 },
          pt: 0.7,
          pb: 1.2,
          bgcolor: '#fcf8f2',
          borderRadius: 2.5,
          backgroundImage: 'linear-gradient(180deg, #fdfaf6 0%, #f7efe4 100%)',
        }}
      >
        {Object.entries(alphabetGroups).map(([letter, keys]) => (
          <Box key={letter} sx={{ mb: 1.5 }}>
            {/* ── alphabet divider ── */}
            <Box display='flex' alignItems='center' gap={1} mb={0.75}>
              <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#b07a5a', letterSpacing: '0.06em', flexShrink: 0 }}>
                {letter}
              </Typography>
              <Divider sx={{ flex: 1, borderColor: '#d9c4ae' }} />
            </Box>

            {/* ── cards ── */}
            <Grid container spacing={1}>
              {keys.map((key) => (
                <Grid item xs={12} sm={6} key={key}>
                  <SaleCard
                    groupKey={key}
                    groupData={sales[key]}
                    onOpenMenu={onOpenMenu}
                    canMenu={canMenu(key)}
                    onOpenDetail={setDetailKey}
                    onPreviewTicket={handlePreviewTicket}
                    showPaidWatermark={showPaidWatermark && sales[key]?.sales?.[0]?.status === STATUS_SALE.FINISHED}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {groupKeys.length === 0 && (
          <Box py={4} textAlign='center'>
            <Typography sx={{ fontSize: '0.8rem', color: '#bbb' }}>Sin pedidos</Typography>
          </Box>
        )}
      </Box>

      {detailKey && sales[detailKey] && (
        <DetailDialog
          groupKey={detailKey}
          groupData={sales[detailKey]}
          onDeleteItem={onDeleteItem}
          canDelete={canDeleteItem ? canDeleteItem(detailKey) : false}
          onClose={() => setDetailKey(null)}
          onDeliverHeld={onDeliverHeld}
        />
      )}

      <TicketPreviewModal
        storeId={previewSale?.storeId}
        idSales={previewSale?.idSales}
        open={!!previewSale}
        onClose={() => setPreviewSale(null)}
      />
    </>
  );
};

export default CashierSaleGrid;
